import { Chip } from '@mui/material';
import { FilingType } from '@taxassist/api-client';

// Consistent color scheme for filing types across all components
const FILING_TYPE_COLORS: Record<string, string> = {
  '1040': '#1976d2', // Blue - Individual
  '1041': '#388e3c', // Green - Estate/Trust
  '1065': '#f57c00', // Orange - Partnership
  '1120': '#7b1fa2', // Purple - Corporation
  '1120S': '#d32f2f', // Red - S Corporation
};

// User-friendly labels for filing types
const FILING_TYPE_LABELS: Record<string, string> = {
  '1040': 'Individual',
  '1041': 'Estate/Trust',
  '1065': 'Partnership',
  '1120': 'Corporation',
  '1120S': 'S Corporation',
};

interface FilingTypeBadgeProps {
  filingType: FilingType;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined' | 'text';
  showLabel?: boolean; // Show full label vs. just filing type number
}

/**
 * Standardized FilingTypeBadge component for consistent filing type display
 * across all UIs (kanban, tables, details, etc.)
 * 
 * Features:
 * - Consistent color coding for each filing type
 * - Optional user-friendly labels ("Individual" vs "1040")
 * - Configurable size and variant
 * - Accessible with proper contrast
 */
export function FilingTypeBadge({ 
  filingType, 
  size = 'small', 
  variant = 'filled',
  showLabel = false 
}: FilingTypeBadgeProps) {
  const color = FILING_TYPE_COLORS[filingType] || '#666';
  const label = showLabel ? FILING_TYPE_LABELS[filingType] || filingType : filingType;
  
  if (variant === 'outlined') {
    return (
      <Chip
        label={label}
        size={size}
        variant="outlined"
        sx={{
          borderColor: color,
          color: color,
          fontWeight: 500,
          minWidth: 60,
          '& .MuiChip-label': {
            px: 1.5,
          },
        }}
      />
    );
  }
  
  if (variant === 'text') {
    return (
      <Chip
        label={label}
        size={size}
        sx={{
          backgroundColor: 'transparent',
          color: color,
          fontWeight: 600,
          border: 'none',
          minWidth: 60,
          '& .MuiChip-label': {
            px: 1,
          },
        }}
      />
    );
  }

  // Default filled variant
  return (
    <Chip
      label={label}
      size={size}
      sx={{
        backgroundColor: color,
        color: 'white',
        fontWeight: 500,
        minWidth: 60,
        '& .MuiChip-label': {
          px: 1.5,
        },
        // Ensure good contrast for accessibility
        '&:hover': {
          backgroundColor: color,
          filter: 'brightness(0.9)',
        },
      }}
    />
  );
}

/**
 * Get filing type color for use in other components
 */
export function getFilingTypeColor(filingType: FilingType): string {
  return FILING_TYPE_COLORS[filingType] || '#666';
}

/**
 * Get filing type label for use in other components
 */
export function getFilingTypeLabel(filingType: FilingType, showFullLabel = false): string {
  return showFullLabel ? (FILING_TYPE_LABELS[filingType] || filingType) : filingType;
}

/**
 * Export the color and label mappings for use in other components
 */
export { FILING_TYPE_COLORS, FILING_TYPE_LABELS };